//garmentSlot.ts — which body region a garment replaces, and how to phrase that to Lucy

export const GARMENT_SLOTS = [
	'top',
	'bottoms',
	'outerwear',
	'dress',
	//Decart groups shoes under BOTTOMS and gates them on the lower body being in frame.
	'footwear',
	'headwear',
	'accessory',
] as const

export type GarmentSlot = (typeof GARMENT_SLOTS)[number]

export const SLOT_LABEL: Record<GarmentSlot, string> = {
	top: 'Top',
	bottoms: 'Bottoms',
	outerwear: 'Outerwear',
	dress: 'Dress',
	footwear: 'Footwear',
	headwear: 'Headwear',
	accessory: 'Accessory',
}

//Decart's docs use generic referents ("the current top") so a prompt works without
//knowing what the person already has on.
const SLOT_REFERENT: Record<GarmentSlot, string> = {
	top: 'the current top',
	bottoms: 'the current bottoms',
	outerwear: 'the current outer layer',
	dress: 'the current outfit',
	footwear: 'the current shoes',
	headwear: 'the current headwear',
	accessory: 'the current accessory',
}

//The docs offer "Add X to the person" for items with nothing to replace, but Decart's own
//shipped catalogue substitutes even for hats, so only loose accessories use the add pattern.
const SLOT_USES_ADD_PATTERN: Record<GarmentSlot, boolean> = {
	top: false,
	bottoms: false,
	outerwear: false,
	dress: false,
	footwear: false,
	headwear: false,
	accessory: true,
}

export const referent = (slot: GarmentSlot): string => SLOT_REFERENT[slot]

export const usesAddPattern = (slot: GarmentSlot): boolean => SLOT_USES_ADD_PATTERN[slot]

//Positions the garment when several are described in one sentence, since "substitute the
//current outfit with X and Y" is ambiguous without body anchors.
export function outfitClause(slot: GarmentSlot, detail: string): string {
	switch (slot) {
		case 'top':
			return `${detail} on top`
		case 'bottoms':
			return `${detail} on the bottom`
		case 'outerwear':
			return `${detail} worn over the top`
		case 'footwear':
			return `${detail} on the feet`
		case 'headwear':
			return `${detail} on the head`
		case 'dress':
		case 'accessory':
			return detail
	}
}

//Head-to-toe order, so the composited reference and the sentence agree.
export const slotOrder = (slot: GarmentSlot): number => GARMENT_SLOTS.indexOf(slot)
